const BASE = '/s/art/jewish_icons';

const NAV_LINKS = [
  { key: 'tefillah', href: `${BASE}?pub_jewish_life_subject=has_any%3Atefillah`, title: 'Tefillah & Davening' },
  { key: 'shabbos', href: `${BASE}?pub_jewish_life_subject=has_any%3Ashabbos`, title: 'Shabbos, Yom Tov & Celebration' },
  { key: 'torah', href: `${BASE}?pub_jewish_life_subject=has_any%3Atorah`, title: 'Jewish Life, Torah & Spirit' },
];

const FAQ_ITEMS = [
  {
    question: 'What is Jewish life and tradition art?',
    answer: [
      'Jewish life and tradition art shows scenes from Jewish life and tradition, such as davening, Shabbos candles, Yom Tov scenes, Torah learning, chuppahs, dancing, children, shul life, and everyday Jewish life. It is different from general Jewish art because it focuses on lived scenes and recognisable Jewish subjects rather than only symbols, places, or decorative themes.',
    ],
  },
  {
    question: 'What types of Jewish life art are available?',
    answer: [
      'Jewish life art can include tefillah and davening scenes, Shabbos and Yom Tov art, Jewish celebration paintings, Torah and biblical scenes, family moments, shul scenes, Chassidic dancing, Klezmer pieces, and modern or abstract works based on Jewish identity and tradition.',
    ],
  },
  {
    question: 'What is a good Jewish painting for a home?',
    answer: [
      'A good Jewish painting for a home usually has a subject that feels meaningful and easy to live with. Shabbos candles, family scenes, davening in shul, Torah scenes, Jewish children, or celebration scenes can all work well. The best choice depends on whether you want the room to feel calm, warm, festive, traditional, or more contemporary.',
    ],
  },
  {
    question: 'What Jewish art makes a good bar mitzvah or bat mitzvah gift?',
    answer: [
      'For a bar mitzvah or bat mitzvah gift, Torah, tefillah, shul, Simchas Torah, or Jewish celebration art can work well. These subjects connect naturally to growth, learning, mitzvos, and Jewish identity. If you know the child\'s family, school, community, or personal style, choose a piece that feels connected without being too narrow.',
    ],
  },
  {
    question: 'Is Shabbos or Yom Tov art good for a housewarming gift?',
    answer: [
      'Yes. Shabbos and Yom Tov art can make a strong housewarming gift because it feels connected to the rhythm of a Jewish home. Candle-lighting pieces, Shabbos table scenes, Chanukah lights, Sukkos scenes, and Jewish celebration art often feel warm, meaningful, and easier to place than highly personal artwork.',
    ],
  },
  {
    question: 'Can I commission a specific Jewish life or tradition painting?',
    answer: [
      'Yes. If you have a specific scene, subject, or occasion in mind, you can message an artist directly through their profile or submit a request through the <a href="/artist/custom_co_studio">commissions hub</a>, where you can specify the subject, mood, size, and style of the piece. Prayer scenes, Shabbos pieces, biblical scenes, and family moments are all available as commissioned work.',
    ],
  },
];

const getSchema = rootURL => {
  const pageURL = `${rootURL}/s/art/jewish_icons`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${pageURL}#collectionpage`,
      url: pageURL,
      name: 'Jewish Life & Tradition Art',
      description:
        'Shop Jewish life art by independent Jewish artists, including tefillah and davening scenes, Shabbos and Yom Tov art, Jewish celebrations, Torah and biblical scenes, and everyday Jewish life.',
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Jewish life art' },
        { '@type': 'Thing', name: 'Jewish tradition art' },
        { '@type': 'Thing', name: 'Tefillah art' },
        { '@type': 'Thing', name: 'Davening art' },
        { '@type': 'Thing', name: 'Jewish prayer painting' },
        { '@type': 'Thing', name: 'Shabbos art' },
        { '@type': 'Thing', name: 'Shabbat painting' },
        { '@type': 'Thing', name: 'Yom Tov art' },
        { '@type': 'Thing', name: 'Jewish celebration art' },
        { '@type': 'Thing', name: 'Torah scenes' },
        { '@type': 'Thing', name: 'Jewish figurative art' },
        { '@type': 'Thing', name: 'Jewish life paintings' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
      mainEntity: {
        '@type': 'ItemList',
        name: 'Jewish life and tradition art collection',
        itemListOrder: 'https://schema.org/ItemListUnordered',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Tefillah & Davening',
            description:
              'Shul scenes, personal prayer, Yom Kippur, Kiddush Levana, children davening, rabbanim in prayer, and minyan scenes.',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Shabbos, Yom Tov & Celebration',
            description:
              'Shabbos candles, Hadlakat Neirot, Chanukah lights, Megillah, Sukkos, Simchas Torah, Chassidim dancing, Klezmer scenes, hachnasas Sefer Torah, chuppahs, and family blessings.',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Jewish Life, Torah & Spirit',
            description:
              'Jewish street scenes, children, community life, Matan Torah, Krias Yam Suf, Bet Hamikdash, soldiers, identity pieces, and abstract Jewish works.',
          },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${pageURL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${rootURL}` },
        { '@type': 'ListItem', position: 2, name: 'Art', item: `${rootURL}/s/art` },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Jewish Life & Tradition Art',
          item: pageURL,
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${pageURL}#faq`,
      mainEntity: FAQ_ITEMS.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer[0].replace(/<[^>]*>/g, ''),
        },
      })),
    },
  ];
};

const jewishIconsSeoConfig = {
  key: 'jewish_icons',
  match: ({ listingType, activeCollection }) =>
    listingType === 'art' && activeCollection === 'jewish_icons',
  accentColor: '--colorArt',
  links: NAV_LINKS,
  title: 'Jewish Life Art | Tefillah, Shabbos & Yom Tov Scenes | Joodaic',
  description:
    'Shop Jewish life art by independent Jewish artists, including tefillah and davening scenes, Shabbos and Yom Tov art, Jewish celebrations, Torah and biblical scenes, and everyday Jewish life.',
  ogImage: {
    url: 'https://sharetribe.imgix.net/67c4adcd-cbbe-48c1-a752-e48bba4bb050/686d472d-ef68-4d53-ac82-d065a370582f?auto=format&fit=clip&h=2400&w=2400&s=33a10a0219945dd476cca83a6e87106f',
    width: '2400',
    height: '2400',
  },
  getSchema,
  intro: {
    heading: 'Jewish Life & Tradition Art',
    tagline:
      'Jewish life paintings and prints, from davening in shul and Shabbos candles to Yom Tov scenes, simchos, Torah and biblical scenes, and everyday Jewish life.',
    collapsedHeading: 'Jewish Life & Tradition Art',
    bodyParagraphs: [
      'Jewish life is made of moments: davening in shul, candles before Shabbos, children saying a tefillah, Chassidim dancing, a chuppah, an Erev Yom Tov market, or a quiet street in Yerushalayim.',
      'This collection brings those moments together through Jewish life art by independent Jewish artists, including tefillah and davening scenes, Shabbos and Yom Tov pieces, Jewish celebration art, Torah and biblical scenes, and everyday Jewish life.',
    ],
    linksLabel: 'Browse by subject',
  },
  bottomSections: [
    {
      id: 'tefillah',
      title: 'Tefillah & Davening',
      paragraphs: [
        'Best for shul scenes, personal prayer, and moments of connection: davening, tefillah, Yom Kippur, Kiddush Levana, morning prayers, children davening, rabbanim in prayer, and minyan scenes.',
        'These pieces often work well in a study, dining room, hallway, office, shul, or anywhere you want the art to carry a quieter sense of kavod and reflection.',
      ],
    },
    {
      id: 'shabbos',
      title: 'Shabbos, Yom Tov & Celebration',
      paragraphs: [
        'Best for warmth, movement, and simcha: Shabbos candles, Hadlakat Neirot, Chanukah lights, Megillah, Sukkos, arba minim, Simchas Torah, Chassidim dancing, Klezmer scenes, hachnasas Sefer Torah, chuppahs, and family blessings.',
        'These pieces are often chosen for homes that want art with energy, memory, and a lived Jewish rhythm. They also work well as meaningful gifts for a new home, bar mitzvah, wedding, or family milestone.',
      ],
    },
    {
      id: 'jewish_life_torah',
      title: 'Jewish Life, Torah & Spirit',
      paragraphs: [
        'Best for everyday Jewish scenes, Torah and biblical subjects, and more reflective or symbolic works: Jewish street life, Yerushalmi children, Erev Yom Tov markets, Matan Torah, Krias Yam Suf, Bet Hamikdash, Acheinu pieces, soldiers, and abstract works rooted in Jewish identity.',
        'This section brings together pieces that are not only about one ritual or occasion. Some show daily Jewish life. Some show Torah or biblical scenes. Others express memory, peoplehood, longing, or belonging in a more symbolic way.',
      ],
    },
    {
      id: 'choosing',
      title: 'Choosing Jewish life art by subject',
      paragraphs: [
        'Jewish life art can be chosen by the moment you want to bring into the room.',
        'For a quieter piece, look at tefillah, davening, shul, or Yom Kippur scenes. These tend to feel thoughtful, grounded, and easy to live with.',
        'For warmth and energy, look at Shabbos candles, Yom Tov scenes, Chassidim dancing, Simchas Torah, Klezmer pieces, or chuppah artwork. These pieces carry movement and celebration.',
        'For a broader Jewish story, look at Torah and biblical scenes, Matan Torah, Krias Yam Suf, Bet Hamikdash, Jewish street scenes, children, soldiers, or abstract works built around Jewish identity and spirit.',
      ],
    },
    {
      id: 'differentiation',
      title: 'What makes this different from general Jewish art?',
      paragraphs: [
        'General Jewish art can include many subjects: Jerusalem, the Kotel, Gedolim portraits, Judaica still life, or abstract Judaic themes.',
        'Jewish life and tradition art is more scene-based. It shows Jewish life happening: someone davening, a family lighting candles, children learning or saying tefillah, people dancing, a shul at neitz, an Erev Yom Tov market, a chuppah, or a scene from Torah and Jewish tradition.',
        'The focus is not only what the piece shows. It is the moment it captures.',
      ],
    },
    {
      id: 'formats',
      title: 'Prints, originals, and mixed media',
      paragraphs: [
        'This collection may include original paintings, fine art prints, canvas pieces, framed artwork, and mixed media.',
        'Prints are usually the most accessible entry point. Original paintings are one-of-a-kind works made directly by the artist. Mixed-media pieces often sit between the two, with hand-finished details, texture, paint, metallic elements, or other additions that give the piece more depth than a standard print.',
        'Check each listing for size, format, framing, and whether the piece is original, printed, or hand-finished.',
      ],
      paragraphsAfter: [
        'For artwork focused on Yerushalayim, the Kotel, Kever Rachel, or Old City scenes, browse <a href="/s/art/the_classics">Jerusalem &amp; Kotel art</a>.',
        'For portraits of rabbanim, Rebbes, and Torah leaders, browse <a href="/s/art/gedolim">Gedolim portraits and paintings</a>.',
        'Looking for a specific scene, family moment, or Jewish subject? You can <a href="/artist/custom_co_studio">commission a custom Jewish painting</a> from a Joodaic artist.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: FAQ_ITEMS,
};

export default jewishIconsSeoConfig;
